import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import { BRAND } from "@/data/content";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      "GetWellSoon is a medical travel coordination company. We help international patients and families coordinate hospital appointments, medical travel, accommodation, and on-ground support in India. We are not a hospital, clinic, or medical provider, and we do not provide medical advice, diagnosis, or treatment.",
    ],
  },
  {
    title: "2. Information we collect",
    body: [
      "When you contact us through this website, we collect the information you choose to share with us:",
      "Contact form: your full name, email address, phone or WhatsApp number, country, the treatment area you are asking about, and the message you write.",
      "WhatsApp and email: if you message us on WhatsApp or by email, we receive your phone number or email address and the contents of your messages.",
      "Medical reports: if you choose to share medical reports with us later in the coordination process, we receive those documents only because you sent them to us.",
      "We do not collect information about you automatically beyond standard, technical server logs, and this website does not use advertising or analytics cookies.",
    ],
  },
  {
    title: "3. How we use your information",
    body: [
      "We use the information you share only to:",
      "Respond to your inquiry and communicate with you about your request.",
      "Coordinate with hospitals, physicians, travel partners, and accommodation providers on your behalf.",
      "Arrange the support services you ask for, such as appointment scheduling, travel planning, airport pickup, and accommodation.",
      "Meet legal, regulatory, or record-keeping obligations where required.",
      "We do not sell your personal information, and we do not use it for advertising.",
    ],
  },
  {
    title: "4. Sharing with hospitals — only with your consent",
    body: [
      "Your medical reports and personal details are shared with a hospital or physician only after you ask us to, or approve the sharing, as part of evaluating treatment options or planning your care. Hospitals and physicians who receive your information are responsible for their own handling of it under their own privacy policies and applicable law.",
      "We may also share limited logistical details (such as your name and arrival time) with drivers, hotels, or interpreters strictly as needed to deliver the services you requested.",
    ],
  },
  {
    title: "5. International data transfers",
    body: [
      "GetWellSoon serves international patients and coordinates with hospitals in India. By using this website and contacting us, you understand that the information you provide may be transferred to, stored, and processed in countries other than your own — including India and the countries where our website and email infrastructure operate — where data protection rules may differ from those in your country.",
    ],
  },
  {
    title: "6. How long we keep your information",
    body: [
      "We keep inquiry records for as long as needed to respond to you, provide coordination services, and maintain reasonable business records. When information is no longer needed, we delete it. You may ask us to delete your information at any time (see Section 8).",
    ],
  },
  {
    title: "7. How we protect your information",
    body: [
      "We apply reasonable technical and organizational measures to protect the information you share with us, including restricting access to inquiry records to authorized coordinators. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    title: "8. Your choices and deletion requests",
    body: [
      `You may ask us at any time to access, correct, or delete the personal information we hold about you. To make a request, email us at ${BRAND.email} or message us on WhatsApp at ${BRAND.whatsappDisplay}. We will respond to genuine requests within a reasonable timeframe.`,
      "Please note that deleting your information may prevent us from continuing to coordinate services for you.",
    ],
  },
  {
    title: "9. Children",
    body: [
      "This website is intended for adults. We do not knowingly collect personal information directly from children. Where a patient is a minor, a parent or legal guardian should contact us and provide information on the patient's behalf.",
    ],
  },
  {
    title: "10. Medical disclaimer",
    body: [
      "GetWellSoon provides coordination and support services only. We do not provide medical advice, diagnosis, or treatment. All medical decisions, treatment plans, and clinical care are the sole responsibility of the treating hospital and healthcare professionals. Nothing on this website or in our communications should be understood as medical advice.",
    ],
  },
  {
    title: "11. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. The current version is always published on this page, and continued use of the website after an update means you accept the revised policy.",
    ],
  },
  {
    title: "12. Contact us",
    body: [
      `For any privacy question or request, contact us at ${BRAND.email} or on WhatsApp at ${BRAND.whatsappDisplay}.`,
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy | GetWellSoon"
        description="How GetWellSoon collects, uses, and protects your personal information — contact forms, WhatsApp and email communications, consent-based hospital sharing, retention, and deletion requests."
      />

      <section className="bg-pearl" data-testid="privacy-hero">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Chapter index="01" label="Legal" />
          <MaskedLines
            delay={0.1}
            className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            lines={[
              "Privacy",
              <span key="l2">
                <span className="italic text-brand">Policy</span>.
              </span>,
            ]}
          />
          <FadeIn delay={0.4}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Your trust matters more than anything else we do. This policy
              explains, in plain language, what information we collect, why we
              collect it, who we share it with, and the choices you have.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="privacy-content">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          {SECTIONS.map((s, i) => (
            <FadeIn
              key={s.title}
              delay={Math.min(i * 0.04, 0.3)}
              className="border-t border-border py-10 first:border-t-0 first:pt-0"
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className="font-serif text-2xl tracking-tight text-ink">
                {s.title}
              </h2>
              <div className="mt-4 space-y-3">
                {s.body.map((p, j) => (
                  <p
                    key={j}
                    className="text-base leading-relaxed text-muted-foreground"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </FadeIn>
          ))}

          <FadeIn className="mt-14 rounded-2xl border border-border bg-white p-6 md:p-8" data-testid="privacy-updated">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              Last updated
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              August 2026 · Applies to {BRAND.domain} and all GetWellSoon
              coordination services.
            </p>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
